import React, { useRef, useState } from 'react';
import api from '../../services/api.js';
import { useToast } from '../../context/ToastContext.jsx';
import { ik } from '../../utils/imagekit.js';

// Shrink a photo in the browser before upload so pages stay fast
function resize(file, max = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('That file is not a valid image'));
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// value: array of image URLs. multiple=false keeps a single image.
export default function ImageField({ value, onChange, multiple = true, max = 6 }) {
  const notify = useToast();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const list = value || [];

  const add = (u) => onChange(multiple ? [...list, u].slice(0, max) : [u]);

  const pick = async (e) => {
    const files = [...e.target.files].slice(0, multiple ? max - list.length : 1);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    try {
      let next = multiple ? [...list] : [];
      for (const f of files) {
        const { url: uploaded } = await api.upload(await resize(f));
        next = multiple ? [...next, uploaded] : [uploaded];
      }
      onChange(next.slice(0, multiple ? max : 1));
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addUrl = () => {
    const u = url.trim();
    if (!/^https?:\/\//i.test(u) && !u.startsWith('/')) { notify('Enter a full image link starting with https://', 'error'); return; }
    add(u);
    setUrl('');
  };

  const move = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="imgfield">
      {list.length > 0 && (
        <div className="imgfield__grid">
          {list.map((u, i) => (
            <div key={u + i} className="imgfield__item">
              <img src={ik(u, 300)} alt={`Image ${i + 1}`} />
              <div className="imgfield__actions">
                {multiple && <button type="button" onClick={() => move(i, -1)} disabled={i === 0}>Earlier</button>}
                {multiple && <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1}>Later</button>}
                <button type="button" onClick={() => onChange(list.filter((_, k) => k !== i))}>Remove</button>
              </div>
              {multiple && i === 0 && <span className="imgfield__tag">Main image</span>}
            </div>
          ))}
        </div>
      )}
      {(multiple ? list.length < max : true) && (
        <div className="imgfield__add">
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple={multiple} onChange={pick} hidden />
          <button type="button" className="abtn" onClick={() => input.current.click()} disabled={busy}>
            {busy ? 'Uploading' : list.length && !multiple ? 'Replace image' : 'Upload image'}
          </button>
          <span className="or">or</span>
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste image link" />
          <button type="button" className="abtn abtn--ghost" onClick={addUrl}>Add link</button>
        </div>
      )}
    </div>
  );
}
